import axios from 'axios';

import { showToast } from "../../components/ToastConfig";

// 創建 axios 實例
const api = axios.create({
  baseURL: process.env.REACT_APP_BASIC_API,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const STATUS_MESSAGES = {
  // HTTP Status 相關
  401: '請重新登入',
  403: '無權限存取',
  404: '找不到資源',
  500: '系統錯誤，請稍後再試',
  502: '系統維護中',
  504: '請求逾時，請稍後再試',
  
  // 業務邏輯相關
  '1001': '信箱為必填',
  '1002': '信箱格式錯誤',
  '1003': '信箱已被使用，請換一個信箱',
  '1004': '職位為必填',
  '1011': '密碼為必填',
  '1012': '確認密碼為必填',
  '1013': '密碼與確認密碼不相符',
  '1014': '密碼更新失敗',
  '1021': '角色為必填',
  '1022': '角色不存在',
  '1023': '角色錯誤',
  '1031': 'Token 無效',
  '1032': 'Token 無效',
  '1033': 'Token 已過期，請重新獲取驗證信',
  '1034': 'Token 無效，請重新獲取驗證信',
  '1050': '建立帳號失敗',
  '1060': '帳號尚未驗證，請至信箱驗證',
  '1061': '帳號已驗證',
  '1062': '帳號已被禁用，請聯繫管理員',
  '1063': '帳號更新失敗',
  '1064': '帳號不存在',
  '1070': '帳號或密碼錯誤。新建帳號者請至信箱收驗證信',
  '1100': '發生錯誤，請稍後再試',
  '1101': '發生錯誤，請稍後再試',
};

// base64 轉換
function base64Encoding(username, password) {  
  // 使用 UTF-8 編碼處理非 ASCII 字符
  const safeBase64 = (str) => {
    try {
      return btoa(str);
    } catch (e) {
      // 如果包含非 ASCII 字符，使用 UTF-8 處理
      return btoa(unescape(encodeURIComponent(str)));
    }
  };
  
  const usernameEncoded = safeBase64(username);
  const passwordEncoded = safeBase64(password);

  // 截取密碼前 10 位再進行編碼
  // const passwordEncoded = btoa(password.slice(0, 10));

  return { email: usernameEncoded, abcd: passwordEncoded };
}

// 處理 API 回應的通用函數
const handleApiResponse = async (apiCall, defaultErrorMsg, defaultSuccessMsg = null) => {
  try {
    const response = await apiCall;
    
    // 成功情況
    if (!response.data.status) {
      if (defaultSuccessMsg) {
        showToast.success(defaultSuccessMsg);
      }
      return { success: true, data: response.data };
    }
    
    // 處理業務邏輯錯誤
    const errorMessage = STATUS_MESSAGES[response.data.status] || defaultErrorMsg;
    showToast.error(errorMessage);
    return { success: false, error: errorMessage, status: response.data.status };
    
  } catch (error) {
    console.log("error", error);

    // 處理 HTTP 錯誤
    if (error.response) {
      console.log("error.response", error.response);
      let errorMessage;
      if (error.response?.status === 400) {
        errorMessage = STATUS_MESSAGES[error.response?.data?.errorCode] || "發生錯誤，請稍後再試";
      } else {
        errorMessage = STATUS_MESSAGES[error.response?.status] || "發生錯誤，請稍後再試";
      }
      showToast.error(errorMessage);
      return { success: false, error: errorMessage, status: error.response.status };
    }
    
    // 處理網路錯誤或其他錯誤
    console.error('API error:', error);
    showToast.error('發生錯誤，請稍後再試');
    return { success: false, error: '發生錯誤，請稍後再試' };
  }
};

// 登入表單處理
export const handleLogin = async (username, password, router) => {
  const { email, abcd } = base64Encoding(username, password);
  try {
    const result = await handleApiResponse(
      api.post(`/Account/AdminLogin?email=${email}&abcd=${abcd}`),
      '登入失敗，請稍後再試'
    );
    
    if (result.success) {
      return result.data;
    }
  } catch (error) {
    console.error('Login error:', error);
    showToast.error('發生錯誤，請稍後再試');
  }
};

// 獲取註冊驗證信
export const handleVerifyEmail = async (email, router, setVerificationStatus, setCountdown) => {
  try {
    setVerificationStatus('sending');
    
    const result = await handleApiResponse(
      api.post('/ValidEmail.ashx', { email }),
      '驗證信發送失敗，請稍後重試',
      '請至信箱收取驗證信'
    );

    if (result.success) {
      setVerificationStatus('sent');
    } else {
      setVerificationStatus('error');
    }
    return result?.data;
    
  } catch (error) {
    console.error('Send verification code error:', error);
    setVerificationStatus('error');
    showToast.error('發生錯誤，請稍後再試');
  }
};

// 註冊表單處理
export const handleRegister = async (data, router, login) => {
  try {
    const result = await handleApiResponse(
      api.post('/RegisterUser.ashx', data),
      '註冊失敗，請稍後再試',
      '註冊成功'
    );
    
    if (result.success) {
      setTimeout(() => {
        router.push('/login');
      }, 1000);
    }
  } catch (error) {
    console.error('Register error:', error);
    showToast.error('發生錯誤，請稍後再試');
  }
};

// 獲取重設密碼驗證信
export const handleForgotPassword = async (email, router, setVerificationStatus, setCountdown) => {
  if (!email || !email.includes('@')) {
    showToast.error('請輸入有效的電子信箱');
    return;
  }

  try {
    setVerificationStatus('sending');
    
    const result = await handleApiResponse(
      api.post('/Account/Admin/ForgetPassword', { email }),
      '驗證信發送失敗，請稍後重試',
      '請至信箱收取驗證信'
    );
    
    if (result.success) {
      setVerificationStatus('sent');
    } else {
      setVerificationStatus('error');
    }
  } catch (error) {
    console.error('Send verification code error:', error);
    setVerificationStatus('error');
    showToast.error('發生錯誤，請稍後再試');
  }
};

// 重設密碼
export const handleResetPassword = async (data) => {
  try {
    const result = await handleApiResponse(
      api.post('/Account/Admin/ResetPassword', data),
      '密碼重設失敗，請稍後再試',
      '密碼重設成功'
    );
    
    if (result.success) {
      return { success: true };
    }
  } catch (error) {
    console.error('Reset password error:', error);
    showToast.error('發生錯誤，請稍後再試');
  }
};